import type { StringLiteral } from "@bbj-types/utils";
import { type ISuspenseHandlerProps, SuspenseHandler } from "@navigations/SuspenseHandler";
import type { ComponentType } from "react";

export interface ISuspenseWrapperProps<
  SuspenseValue,
  SuspenseHookArgs extends unknown[],
  SuspenseHook extends (...args: SuspenseHookArgs) => () => SuspenseValue,
  TargetProps extends string,
  TargetComponentProps extends { [Key in StringLiteral<TargetProps>]: SuspenseValue },
> {
  useSuspenseHook: SuspenseHook;
  suspenseHookArgs: SuspenseHookArgs;
  targetProps: StringLiteral<TargetProps>;
  TargetComponent: ComponentType<TargetComponentProps>;
  targetComponentProps: Omit<TargetComponentProps, StringLiteral<TargetProps>>;
  slotProps?: ISuspenseHandlerProps["slotProps"];
}

export const SuspenseWrapper = <
  SuspenseValue,
  SuspenseHookArgs extends unknown[],
  SuspenseHook extends (...args: SuspenseHookArgs) => () => SuspenseValue,
  TargetProps extends string,
  TargetComponentProps extends { [Key in StringLiteral<TargetProps>]: SuspenseValue },
>({
  useSuspenseHook,
  suspenseHookArgs,
  targetProps,
  TargetComponent,
  targetComponentProps,
  slotProps,
}: ISuspenseWrapperProps<SuspenseValue, SuspenseHookArgs, SuspenseHook, TargetProps, TargetComponentProps>) => {
  const getSuspenseValue = useSuspenseHook(...suspenseHookArgs);

  const props = {
    ...targetComponentProps,
    [targetProps]: getSuspenseValue,
  } as TargetComponentProps;

  return (
    <SuspenseHandler slotProps={slotProps}>
      <TargetComponent {...props} />
    </SuspenseHandler>
  );
};
